import {
  CustomReward,
  CustomRewardCurrency,
  FlexBox,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import useRewards from "./hooks/useRewards"
import useRewardsUserSelectStore from "./hooks/useRewardsUserSelectStore"
import PurchasedRewardGroup from "./redeem/PurchasedRewardGroup"
import RewardRedeemCard from "./redeem/RewardRedeemCard"
import ScreenTimeRestrictionsSection from "./redeem/ScreenTimeRestrictionsSection"
import TotalCoinsEarnedCard from "./redeem/TotalCoinsEarnedCard"
import { RewardsEmpty } from "./RewardsEmpty"
import { LoadingItemsSkeleton } from "../../components/skeleton/Skeleton"
import {
  useCustomRewardBalancesQuery,
  useCustomRewardsQuery,
} from "../../networking/rewards/queries"
import { useRouter } from "../../util/router"
import CoinsReadyToRedeemCard from "../family/coinTransactions/CoinsReadyToRedeemCard"

const RewardsRedeem = () => {
  const { selectedUserId } = useRewardsUserSelectStore()
  const {
    hasPreviouslyPurchasedOrRedeemedRewards,
    childRewardBalances,
    selectedChild,
    childHasAvailableRewardsToPurchase,
    childHasScreenTimeControls,
    isLoading,
  } = useRewards()
  const { data: rewards } = useCustomRewardsQuery()
  const { data: balances } = useCustomRewardBalancesQuery()
  const router = useRouter()
  const { t } = useTranslation()

  const sortedChildRewardBalances = childRewardBalances?.sort(
    (a, b) => b.balance - a.balance
  )

  const rewardsWithBalances = useMemo(() => {
    const rewardIdsToRewardsWithBalances: {
      [key: number]: CustomReward
    } = {}

    rewards?.forEach((reward) => {
      if (!rewardIdsToRewardsWithBalances[reward.id]) {
        rewardIdsToRewardsWithBalances[reward.id] = reward
      }
    })
    balances?.forEach((balance) => {
      rewardIdsToRewardsWithBalances[balance.reward.id] = balance?.reward
    })

    return rewardIdsToRewardsWithBalances
  }, [rewards, balances])

  const { currencyRewards, itemRewards } = useMemo(() => {
    const sortedRewards = Object.values(rewardsWithBalances).sort(
      (a, b) => a?.cost - b?.cost
    )
    const currencyRewards = [] as CustomReward[]
    const itemRewards = [] as CustomReward[]
    sortedRewards.forEach((reward) => {
      if (reward?.currency === CustomRewardCurrency.ITEM) {
        itemRewards.push(reward)
      } else {
        currencyRewards.push(reward)
      }
    })
    return { currencyRewards, itemRewards }
  }, [rewardsWithBalances])

  return (
    <div className="py-3">
      {isLoading ? (
        <LoadingItemsSkeleton />
      ) : !rewards?.length || rewards.length === 0 ? (
        <RewardsEmpty />
      ) : (
        <FlexBox direction="column" gap={SPACING.space4}>
          {selectedUserId && (
            <>
              <div className="w-full flex flex-col gap-1">
                <TotalCoinsEarnedCard childId={selectedUserId} />
                <CoinsReadyToRedeemCard
                  childId={selectedUserId}
                  allowRedeem={false}
                />
                {childHasScreenTimeControls && (
                  <ScreenTimeRestrictionsSection />
                )}
              </div>
              <Typography variant="bodyBold" className="mt-2">
                {t("REWARD_PURCHASED_REWARDS")}
              </Typography>
            </>
          )}
          {selectedUserId === null ? (
            <>
              {currencyRewards.length > 0 && (
                <>
                  <FlexBox
                    direction="row"
                    justify="flex-start"
                    align="center"
                    gap={SPACING.space1}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <img
                      src={"/images/rewards_currency_exchange.png"}
                      alt="Currency Exchange"
                      style={{ minHeight: "32px", maxHeight: "32px" }}
                    />
                    <Typography variant="bodyBold">
                      {t("REWARD_CURRENCY_EXCHANGE")}
                    </Typography>
                  </FlexBox>
                  <FlexBox direction="column" gap={SPACING.space2}>
                    {currencyRewards.map((reward) => (
                      <PurchasedRewardGroup key={reward.id} reward={reward} />
                    ))}
                  </FlexBox>
                </>
              )}
              {itemRewards.length > 0 && (
                <>
                  <Typography
                    variant="bodyBold"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      whiteSpace: "nowrap",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h3">🎈</Typography>{" "}
                    {t("REWARD_ITEM_OR_ACTIVITY")}
                  </Typography>
                  <FlexBox direction="column" gap={SPACING.space2}>
                    {itemRewards.map((reward) => (
                      <PurchasedRewardGroup key={reward.id} reward={reward} />
                    ))}
                  </FlexBox>
                </>
              )}
            </>
          ) : childRewardBalances?.length > 0 ? (
            <FlexBox direction="column" gap={SPACING.space2}>
              {sortedChildRewardBalances.map((balance, i: number) => (
                <RewardRedeemCard key={i} rewardBalance={balance} />
              ))}
            </FlexBox>
          ) : childHasAvailableRewardsToPurchase ? (
            <div className="flex flex-col p-3 rounded-md border border-borderPrimary bg-white gap-2 w-full">
              <Typography variant="bodySmallBold">
                {t("REWARD_NOT_PURCHASED_YET", {
                  name: selectedChild?.user.name,
                })}
              </Typography>
              <Typography variant="bodySmall">
                {t("REWARD_CAN_PURCHASE_HINT")}
              </Typography>
              <img
                src="/images/rewards_empty.png"
                alt="Purchasing rewards"
                className="w-full h-full"
              />
            </div>
          ) : (
            <FlexBox
              justify="center"
              align="center"
              style={{
                padding: SPACING.space3,
                background: JoonUIColor.background.primaryNeutral,
                border: `1px solid ${JoonUIColor.border.default}`,
                borderRadius: SPACING.space2,
              }}
            >
              <Typography variant="bodySmallBold">
                {t("REWARD_NO_CUSTOM_YET")}
              </Typography>
            </FlexBox>
          )}
        </FlexBox>
      )}
      {!isLoading && hasPreviouslyPurchasedOrRedeemedRewards && (
        <button
          style={{
            marginTop: SPACING.space6,
            marginBottom: SPACING.space10,
            width: "100%",
          }}
          onClick={() => router.push("/rewards/transactions")}
        >
          <Typography
            variant="bodySmallBold"
            textAlign="center"
            color={JoonUIColor.semantic.primary}
          >
            {t("REWARD_VIEW_ALL_TRANSACTIONS")}
          </Typography>
        </button>
      )}
    </div>
  )
}

export default RewardsRedeem
